import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import { getSecurityNavItemPath } from 'src/mindtime/config/securityNav';
import type { WazuhVulnerabilityItem } from 'src/types/mindtime/wazuhVulnerabilities';
import {
  EDR_COLORS,
  EdrDateTime,
  EdrDetailEndpointSidebar,
  EdrDetailKeyValueRows,
  EdrDetailSectionCard,
  EdrDetailTimeline,
  EdrExpandableText,
  EdrSeverityPill,
  EdrStatusPill,
  SEVERITY_TONES,
  formatEdrDateTime,
  type EdrDetailTimelineEvent,
  type EdrEntityDetailPageProps,
  type EdrSeverityLevel,
  type EdrStatusTone,
} from '../shared/edrPrimitives';

import { toEdrSeverityLevel, EDR_SEVERITY_FILTER_OPTIONS } from '../shared/edrFormat';

export const vulnerabilitySeverityToLevel = toEdrSeverityLevel;

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const REMEDIATION_CHECKLIST = [
  'Review the vendor advisory and test the patch in staging',
  'Schedule a maintenance window',
  'Apply the update and confirm the version',
  'Re-scan to verify the vulnerability is closed',
] as const;

const normalizeKey = (value: string | undefined): string =>
  (value ?? '').toString().trim().toLowerCase();

export const toVulnerabilityTitle = (value: string | undefined): string => {
  const label = STATUS_LABELS[normalizeKey(value)];
  if (label) return label;
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

/** Filter dropdown labels: Sentence Case; preserves mixed-case names (e.g. "7-Zip"). */
export const toFilterListLabel = (value: string | undefined): string => {
  const text = (value ?? '').toString().trim();
  if (!text) return '';
  const known = STATUS_LABELS[normalizeKey(text)];
  if (known) return known;
  const isUniformCase = text === text.toLowerCase() || text === text.toUpperCase();
  if (isUniformCase) {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
};


export const vulnerabilityStatusToTone = (status: string | undefined): EdrStatusTone => {
  const key = normalizeKey(status);
  if (key === 'open') return 'critical';
  if (key === 'in_progress' || key === 'inprogress') return 'medium';
  if (key === 'resolved' || key === 'closed') return 'success';
  return 'neutral';
};

/** Id for GET /vulnerabilities/{id} — prefers `id`, then `_id`. */
export const getVulnerabilityId = (
  item: Pick<WazuhVulnerabilityItem, 'id' | '_id'> | null | undefined
): string => String(item?.id ?? item?._id ?? '').trim();

const RemediationCard = ({ item }: { item: WazuhVulnerabilityItem }) => {
  const fix = (item.recommendedFix || '').trim();

  return (
    <EdrDetailSectionCard icon={<BuildIcon style={{ fontSize: 16 }} />} title="Recommended Remediation">
      {fix ? (
        <Box mb={1.5}>
          <EdrExpandableText text={fix} lines={3} />
        </Box>
      ) : null}
      <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
        {REMEDIATION_CHECKLIST.map((step) => (
          <Box key={step} display="flex" alignItems="flex-start" style={{ gap: 8 }}>
            <CheckCircleIcon style={{ fontSize: 16, color: EDR_COLORS.critical, marginTop: 2 }} />
            <Typography style={{ fontSize: 12, color: EDR_COLORS.textBody, lineHeight: 1.4 }}>
              {step}
            </Typography>
          </Box>
        ))}
      </Box>
    </EdrDetailSectionCard>
  );
};

const buildTimelineEvents = (item: WazuhVulnerabilityItem): EdrDetailTimelineEvent[] => {
  const status = normalizeKey(item.status);
  const events: EdrDetailTimelineEvent[] = [];

  if (item.publishedAt) {
    events.push({
      id: 'published',
      title: 'CVE published',
      subtitle: 'Added to the National Vulnerability Database.',
      time: formatEdrDateTime(item.publishedAt),
      tone: 'neutral',
    });
  }

  if (item.detectedAt) {
    events.push({
      id: 'detected',
      title: `Detected on ${item.endpoint || 'endpoint'}`,
      subtitle: 'Found during a scheduled vulnerability scan.',
      time: formatEdrDateTime(item.detectedAt),
      tone: 'critical',
    });
  }

  if (status === 'open' || status === 'in_progress' || status === 'inprogress') {
    events.push({
      id: 'awaiting',
      title: 'Awaiting remediation',
      subtitle: 'No fix has been applied yet.',
      time: formatEdrDateTime(item.detectedAt),
      tone: 'critical',
    });
  } else if (item.solvedTime) {
    events.push({
      id: 'resolved',
      title: 'Marked as resolved',
      subtitle: 'Remediation applied and vulnerability closed.',
      time: formatEdrDateTime(item.solvedTime),
      tone: 'neutral',
    });
  }

  return events;
};

export const buildVulnerabilityDetailProps = (
  item: WazuhVulnerabilityItem
): Omit<EdrEntityDetailPageProps, 'loading' | 'notFoundMessage'> => {
  const statusLabel = toVulnerabilityTitle(item.status) || '—';
  const statusTone = vulnerabilityStatusToTone(item.status);
  const severityLevel = vulnerabilitySeverityToLevel(item.severity);
  const severityLabel = toVulnerabilityTitle(item.severity) || '—';
  const score = typeof item.cvssScore === 'number' ? item.cvssScore : 0;
  const scoreLabel = typeof item.cvssScore === 'number' ? item.cvssScore.toFixed(1) : '—';
  const scoreColor = SEVERITY_TONES[severityLevel]?.color ?? EDR_COLORS.textPrimary;
  const timelineEvents = buildTimelineEvents(item);
  const description = (item.title || '').trim();
  const osLabel = toFilterListLabel(item.osPlatform) || '—';

  return {
    backLink: {
      label: 'Back to Vulnerabilities',
      to: getSecurityNavItemPath('vulnerability-detection') ?? '',
    },
    title: item.cveId || 'Vulnerability',
    badges: (
      <>
        <EdrSeverityPill level={severityLevel} label={severityLabel} />
        <EdrStatusPill tone={statusTone} label={statusLabel} />
      </>
    ),
    metaLine: (
      <>
        {item.packageName?.trim() || '—'}
        {' · Detected '}
        {formatEdrDateTime(item.detectedAt)}
        {' on '}
        <strong style={{ color: EDR_COLORS.textPrimary }}>{item.endpoint || '—'}</strong>
      </>
    ),
    main: (
      <>
        {description ? (
          <EdrDetailSectionCard
            icon={<InfoOutlinedIcon style={{ fontSize: 16 }} />}
            title="Description"
          >
            <EdrExpandableText text={description} lines={4} />
          </EdrDetailSectionCard>
        ) : null}

        <EdrDetailSectionCard icon={<SecurityIcon style={{ fontSize: 16 }} />} title="CVSS Score">
          <Box display="flex" alignItems="center" style={{ gap: 14 }}>
            <Box style={{ flexShrink: 0, minWidth: 52 }}>
              <Typography
                style={{
                  fontSize: 26,
                  fontWeight: 700,
                  color: scoreColor,
                  lineHeight: 1.1,
                }}
              >
                {scoreLabel}
              </Typography>
              <Typography
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: EDR_COLORS.textMuted,
                  marginTop: 2,
                }}
              >
                CVSS V3.1
              </Typography>
            </Box>
            <Box flex={1}>
              <Box style={{ height: 8, borderRadius: 4, background: EDR_COLORS.slate200 }}>
                <Box
                  style={{
                    width: `${Math.min(Math.max(score / 10, 0), 1) * 100}%`,
                    height: '100%',
                    borderRadius: 4,
                    background: scoreColor,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </EdrDetailSectionCard>

        <RemediationCard item={item} />

        {timelineEvents.length > 0 ? (
          <EdrDetailSectionCard
            icon={<HistoryIcon style={{ fontSize: 16 }} />}
            title="Related Activity Timeline"
            bodyPadding={false}
          >
            <EdrDetailTimeline events={timelineEvents} />
          </EdrDetailSectionCard>
        ) : null}
      </>
    ),
    sidebar: (
      <>
        <EdrDetailEndpointSidebar
          endpoint={item.endpoint}
          hostName={item.hostName}
          statusLabel={statusLabel}
          statusTone={statusTone}
          organization={item.organization}
          operatingSystem={osLabel}
        />
        <EdrDetailSectionCard
          icon={<InfoOutlinedIcon style={{ fontSize: 16 }} />}
          title="Vulnerability Details"
        >
          <EdrDetailKeyValueRows
            rows={[
              { label: 'CVE ID', value: item.cveId?.trim() || '—', mono: true },
              { label: 'Package Name', value: item.packageName?.trim() || '—' },
              { label: 'Operating System', value: osLabel },
              { label: 'Detected', value: <EdrDateTime value={item.detectedAt} /> },
              {
                label: 'Published',
                value: item.publishedAt ? <EdrDateTime value={item.publishedAt} /> : '—',
              },
              {
                label: 'Solved',
                value: item.solvedTime ? <EdrDateTime value={item.solvedTime} /> : '—',
              },
            ]}
          />
        </EdrDetailSectionCard>
      </>
    ),
  };
};
